import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Spin, Typography } from "antd";
import Table, { ColumnsType } from "antd/es/table";
import { useCoordinator } from "@/shared/lib/hooks";
import { journalsQueries } from "@entities/journal";
import { downloadFile, fallbackString, formatDate } from "@shared/utils";
import "./journals-notes.scss";

interface DataType {
  key: string;
  number: string;
  text: string;
  date?: string;
  author: string;
  deadline?: string;
  documents?: Array<{
    name: string;
    id: string;
  }>;
  value?: string;
  isCurrent?: boolean;
  versionNumber?: number;
}

export const JournalsNotes = () => {
  const { titleId } = useCoordinator();

  const { data: titleData, isPending: isTitleLoading } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );
  const notesQueryEnabled =
    !!titleData?.mainJournal?.mainJournalId &&
    !!titleData?.mainJournal?.titleJournal?.titleNotesListId &&
    !!titleId;

  const { data: journalNotesData, isPending: isJournalNotesLoading } = useQuery(
    {
      ...journalsQueries.getTitleNotes({
        mainJournalId: titleData?.mainJournal?.mainJournalId || "",
        titleNotesListId:
          titleData?.mainJournal?.titleJournal?.titleNotesListId || "",
        titleId,
      }),
      enabled: notesQueryEnabled,
    },
  );

  const isTableLoading =
    isTitleLoading || (notesQueryEnabled && isJournalNotesLoading);

  const [loadingFileIds, setLoadingFileIds] = useState<Record<string, boolean>>(
    {},
  );

  const setLoading = (fileId: string, loading: boolean) => {
    setLoadingFileIds((prev) => ({ ...prev, [fileId]: loading }));
  };

  // @ts-expect-error commentsListItem не совпадает с типом DataType
  const TableData: DataType[] =
    journalNotesData?.commentsList?.commentsListItem?.map((item) => ({
      key: fallbackString(item.comment?.commentID),
      number: fallbackString(String(item?.sequenceNumber)),
      text: fallbackString(item.datasetNote?.noteText),
      date: fallbackString(item.datasetNote?.noteDate),
      author: [
        item.remarkAuthor?.lastName,
        item.remarkAuthor?.firstName,
        item.remarkAuthor?.middleName,
        item.remarkAuthor?.position,
        item.remarkAuthor?.organizationName,
      ]
        .filter(Boolean)
        .join(" "),
      deadline: fallbackString(item.deadline),
      documents:
        item.datasetNote?.attachedDocumentsList?.attachedDocuments?.map(
          (doc) => {
            const file =
              doc.variantRequiredFileWithInternalSignature
                ?.fileWithDisattachSignature?.file ||
              doc.variantRequiredFileWithDisattachSignature
                ?.fileWithDisattachSignature?.file;

            return {
              name: fallbackString(file?.name),
              id: fallbackString(file?.id),
            };
          },
        ) || [],
      value: fallbackString(item.datasetNote?.commentedValue),
      isCurrent: item.isCurrent,
      versionNumber: item.versionNumber,
    })) || [];

  const columns: ColumnsType<DataType> = [
    {
      title: "Номер/Версия ТЛ",
      dataIndex: "number",
      key: "number",
      width: 130,
      sorter: (a, b) => +a.number - +b.number,
      render: (_, { number, versionNumber, isCurrent }) => (
        <div className="journals-notes-number">
          <div className="journals-notes-number__line">
            <span className="journals-notes-number__value">№ {number}</span>
            {isCurrent && (
              <span className="journals-notes-number__dot" aria-hidden />
            )}
          </div>
          <div className="journals-notes-number__version">
            Версия {versionNumber || "-"}
          </div>
        </div>
      ),
    },
    {
      title: "Автор замечания",
      dataIndex: "author",
      key: "author",
      width: 200,
      render: (_, { author }) => <div>{author || "-"}</div>,
    },
    {
      title: "Дата формирования",
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (_, { date }) => <div>{fallbackString(formatDate(date)) || "-"}</div>,
    },
    {
      title: "Текст замечания",
      dataIndex: "text",
      key: "text",
      render: (_, { text }) => <div>{text}</div>,
    },
    {
      title: "Дата устранения",
      dataIndex: "deadline",
      key: "deadline",
      width: 150,
      render: (_, { deadline }) => (
        <div>{fallbackString(formatDate(deadline)) || "-"}</div>
      ),
    },
    {
      title: "Комментируемое значение",
      dataIndex: "value",
      key: "value",
      width: 120,
      render: (_, { value }) => <div>{value || "-"}</div>,
    },
    {
      title: "Приложенные документы",
      dataIndex: "documents",
      key: "documents",
      render: (_, { documents }) => (
        <div>
          {documents?.map((doc) => {
            return (
              <div className="journals-notes-doc" key={doc.id}>
                {doc.name ? (
                  <div
                    className="journals-notes-doc__link"
                    onClick={() => downloadFile(doc.id as string, setLoading)}
                  >
                    {loadingFileIds[doc.id || ""] ? (
                      <Spin size="small" />
                    ) : (
                      <Button type="link">{fallbackString(doc.name)}</Button>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ),
    },
  ];

  const [title] = useState(() => (
    <div className="journals-notes-header">
      <Typography.Title level={4}>Замечания от органа Надзора</Typography.Title>
      <div className="journals-notes-filter">
        <span className="journals-notes-filter__label">Показывать</span>
        <div className="journals-notes-filter__chip">
          <span className="journals-notes-filter__chip-text">Все</span>
        </div>
      </div>
    </div>
  ));

  if (!isTableLoading && TableData.length === 0) {
    return (
      <div className="table-container">
        <Typography.Title level={4}>Список замечаний из ГСН</Typography.Title>
        <div className="journal-empty-text">
          Сведения не присланы
        </div>
      </div>
    );
  }

  return (
    <div className="table-container journals-notes-table">
      <Table
        columns={columns}
        dataSource={TableData}
        bordered
        pagination={false}
        title={() => title}
        loading={isTableLoading}
        rowClassName={(_, index) =>
          TableData[index]?.isCurrent ? "" : "journals-notes-row--processed"
        }
      />
    </div>
  );
};
